import React, { useState, useRef, Component } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
import { TrendingUp, Clock, Shield, Users, Zap, Target, BarChart3, ArrowUpRight, CheckCircle, Gauge, Briefcase, Building2, Sparkles, Activity, DollarSign, UserCheck, Workflow, Brain, LineChart, FileCheck, Lock, Layers } from 'lucide-react';
// Icon mapping for dynamic icon rendering
const iconMap: Record<string, any> = {
  TrendingUp,
  Clock,
  Shield,
  Users,
  Zap,
  Target,
  BarChart3,
  ArrowUpRight,
  CheckCircle,
  Gauge,
  Briefcase,
  Building2,
  Sparkles,
  Activity,
  DollarSign,
  UserCheck,
  Workflow,
  Brain,
  LineChart,
  FileCheck,
  Lock,
  Layers
};
// Expertise data for each service
const serviceExpertise: Record<string, {
  title: string;
  description: string;
  capabilities: string[];
  caseStudy: {
    industry: string;
    challenge: string;
    solution: string;
  };
  outcomes: Array<{
    icon: string;
    label: string;
  }>;
}> = {
  Agentforce: {
    title: 'Agentforce',
    description: 'We run strategic workshops to map your customer service and sales operations, identifying high-volume, repetitive processes where autonomous AI agents can deliver immediate impact. Our approach designs agent orchestration that fits seamlessly into your existing workflows.',
    capabilities: ['Autonomous agent design and orchestration', 'Natural language processing and intent recognition', 'Multi-channel agent deployment', 'Agent performance monitoring and optimization'],
    caseStudy: {
      industry: 'Financial Services Firm',
      challenge: 'Support team overwhelmed with routine account inquiries, leading to long wait times and inconsistent responses across channels.',
      solution: 'Deployed autonomous agents to handle tier-1 inquiries across email, chat, and phone, with intelligent escalation to human agents for complex cases. Agents now resolve 65% of inquiries without human intervention.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '40% reduction in manual support tasks'
    }, {
      icon: 'Zap',
      label: 'Real-time intelligent decision-making'
    }, {
      icon: 'Users',
      label: 'Seamless human-agent handoffs'
    }, {
      icon: 'Clock',
      label: '24/7 automated customer support'
    }]
  },
  Flow: {
    title: 'Flow Automation',
    description: 'Through collaborative workshops, we analyze your manual processes and design sophisticated Flow automations that orchestrate complex business logic across your Salesforce ecosystem. Each workflow is architected for reliability, scalability, and zero-error execution.',
    capabilities: ['Complex multi-step process automation', 'Cross-object workflow orchestration', 'Screen flows for guided user experiences', 'Integration with external systems'],
    caseStudy: {
      industry: 'Healthcare Provider Network',
      challenge: 'Patient referral process required 12+ manual steps across multiple systems, causing delays and data entry errors.',
      solution: 'Built end-to-end Flow automation connecting patient intake, provider matching, appointment scheduling, and insurance verification. Reduced referral processing time from 3 days to 2 hours.'
    },
    outcomes: [{
      icon: 'Gauge',
      label: '60% faster process completion'
    }, {
      icon: 'CheckCircle',
      label: 'Zero-error data processing'
    }, {
      icon: 'Shield',
      label: 'Consistent business rule enforcement'
    }, {
      icon: 'Workflow',
      label: 'Automated cross-system orchestration'
    }]
  },
  'Omni-Channel': {
    title: 'Omni-Channel',
    description: 'We assess your current customer engagement channels and team capacity, then design intelligent routing strategies that balance workloads and ensure the right work reaches the right person at the right time across all touchpoints.',
    capabilities: ['Intelligent work routing and assignment', 'Multi-channel presence management', 'Capacity-based workload distribution', 'Real-time queue monitoring'],
    caseStudy: {
      industry: 'National Retail Chain',
      challenge: 'Customer inquiries across phone, email, chat, and social media created uneven workloads and inconsistent response times.',
      solution: 'Implemented Omni-Channel routing with skills-based assignment and real-time capacity monitoring. Work now flows intelligently to available agents based on expertise and current workload.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '35% improvement in response times'
    }, {
      icon: 'Users',
      label: 'Balanced team workloads'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced customer satisfaction'
    }, {
      icon: 'Activity',
      label: 'Real-time capacity visibility'
    }]
  },
  'Salesforce Platform': {
    title: 'Salesforce Platform',
    description: 'Our workshops dive deep into your business model to architect the foundational platform that will power your operations for years to come. We design scalable data models, security frameworks, and governance structures that support growth.',
    capabilities: ['Custom object and field architecture', 'Security model design and implementation', 'Platform governance and best practices', 'Scalable data architecture'],
    caseStudy: {
      industry: 'Technology SaaS Company',
      challenge: 'Rapid growth outpaced their initial Salesforce setup, causing data inconsistencies and security gaps across business units.',
      solution: 'Redesigned platform architecture with proper object relationships, role hierarchies, and sharing rules. Established governance framework for sustainable scaling.'
    },
    outcomes: [{
      icon: 'Shield',
      label: 'Enterprise-grade security and compliance'
    }, {
      icon: 'Layers',
      label: 'Scalable system architecture'
    }, {
      icon: 'Target',
      label: 'Future-proof platform foundation'
    }, {
      icon: 'Lock',
      label: 'Robust data governance'
    }]
  },
  'Data Cloud': {
    title: 'Data Cloud',
    description: 'We map your fragmented customer data sources and design a unified Data Cloud strategy that creates a real-time, 360-degree customer view. Our approach harmonizes data from every touchpoint into actionable intelligence.',
    capabilities: ['Multi-source data integration', 'Real-time data harmonization', 'Customer 360 profile creation', 'Data activation across channels'],
    caseStudy: {
      industry: 'Insurance Provider',
      challenge: 'Customer data scattered across policy systems, claims platforms, and marketing tools prevented personalized service and created compliance risks.',
      solution: 'Unified 15+ data sources into Data Cloud with real-time synchronization. Created golden customer records that power personalized experiences across all channels.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Single source of truth for customer data'
    }, {
      icon: 'Zap',
      label: 'Real-time personalization'
    }, {
      icon: 'CheckCircle',
      label: 'Cross-channel data consistency'
    }, {
      icon: 'Brain',
      label: 'AI-ready unified data model'
    }]
  },
  'Sales Cloud': {
    title: 'Sales Cloud',
    description: 'Through discovery workshops with your sales leadership, we design Sales Cloud implementations that accelerate pipeline velocity, eliminate friction, and provide the intelligence your team needs to close deals faster.',
    capabilities: ['Sales process optimization', 'Pipeline and forecast management', 'Territory and quota planning', 'Sales intelligence and analytics'],
    caseStudy: {
      industry: 'Manufacturing Company',
      challenge: 'Sales team spending 40% of time on administrative tasks instead of selling. Forecast accuracy below 60%.',
      solution: 'Streamlined sales processes with automation, built intelligent forecasting models, and implemented territory management. Freed up 15 hours per rep per week.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '25% increase in sales productivity'
    }, {
      icon: 'Target',
      label: 'Improved forecast accuracy'
    }, {
      icon: 'Clock',
      label: 'Faster deal cycles'
    }, {
      icon: 'BarChart3',
      label: 'Data-driven pipeline insights'
    }]
  },
  'Service Cloud': {
    title: 'Service Cloud',
    description: 'We analyze your support operations to design Service Cloud solutions that scale exceptional customer experiences. Our workshops identify bottlenecks and opportunities for automation, self-service, and agent productivity.',
    capabilities: ['Case management optimization', 'Knowledge base design', 'Self-service portal development', 'Service analytics and reporting'],
    caseStudy: {
      industry: 'Software Company',
      challenge: 'Support team drowning in repetitive tickets. Average resolution time of 48 hours. Customer satisfaction declining.',
      solution: 'Built comprehensive knowledge base with AI-powered article recommendations, implemented case deflection flows, and optimized routing. Self-service now resolves 40% of inquiries.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '50% reduction in case resolution time'
    }, {
      icon: 'CheckCircle',
      label: 'Increased first-contact resolution'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced customer satisfaction scores'
    }, {
      icon: 'Users',
      label: 'Improved agent productivity'
    }]
  },
  'Marketing Cloud': {
    title: 'Marketing Cloud',
    description: 'Our strategic sessions map your customer journeys and design Marketing Cloud implementations that deliver personalized, data-driven campaigns across every channel. We build automation that nurtures relationships at scale.',
    capabilities: ['Journey orchestration and automation', 'Multi-channel campaign management', 'Audience segmentation and targeting', 'Marketing analytics and attribution'],
    caseStudy: {
      industry: 'E-commerce Retailer',
      challenge: 'Generic email blasts with 2% engagement. No visibility into customer journey effectiveness or campaign ROI.',
      solution: 'Designed personalized journey orchestration based on behavior, purchase history, and preferences. Built attribution model to track campaign performance.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '3x improvement in engagement rates'
    }, {
      icon: 'Target',
      label: 'Personalized customer journeys'
    }, {
      icon: 'BarChart3',
      label: 'Data-driven campaign optimization'
    }, {
      icon: 'DollarSign',
      label: 'Clear marketing ROI visibility'
    }]
  },
  'Experience Cloud': {
    title: 'Experience Cloud',
    description: 'We collaborate with your teams to design branded digital experiences that connect customers, partners, and employees. Our approach creates self-service portals that reduce support costs while enhancing engagement.',
    capabilities: ['Custom portal and community design', 'Self-service experience optimization', 'Partner relationship management', 'Mobile-responsive experience delivery'],
    caseStudy: {
      industry: 'B2B Distribution Company',
      challenge: 'Partners calling for order status, pricing, and inventory information. Support team handling 200+ calls daily.',
      solution: 'Built partner portal with real-time order tracking, pricing catalogs, and inventory visibility. Partners now self-serve 80% of inquiries.'
    },
    outcomes: [{
      icon: 'DollarSign',
      label: 'Reduced support costs through self-service'
    }, {
      icon: 'Users',
      label: 'Enhanced partner collaboration'
    }, {
      icon: 'Sparkles',
      label: 'Branded, on-brand digital experiences'
    }, {
      icon: 'CheckCircle',
      label: '80% inquiry deflection rate'
    }]
  },
  'Revenue Cloud': {
    title: 'Revenue Cloud',
    description: 'Through workshops with your revenue operations team, we design Revenue Cloud solutions that streamline quote-to-cash processes, eliminate pricing errors, and automate complex billing scenarios for subscription and usage-based models.',
    capabilities: ['CPQ configuration and optimization', 'Subscription and usage-based billing', 'Revenue recognition automation', 'Contract lifecycle management'],
    caseStudy: {
      industry: 'SaaS Platform Provider',
      challenge: 'Complex pricing models causing quote errors. Manual contract amendments taking days. Revenue recognition spreadsheets breaking.',
      solution: 'Implemented Revenue Cloud with automated pricing rules, self-service amendments, and revenue recognition automation. Quotes now generated in minutes with zero errors.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '40% faster quote generation'
    }, {
      icon: 'BarChart3',
      label: 'Accurate revenue forecasting'
    }, {
      icon: 'Workflow',
      label: 'Automated billing and invoicing'
    }, {
      icon: 'CheckCircle',
      label: 'Eliminated pricing errors'
    }]
  },
  'Field Service': {
    title: 'Field Service',
    description: 'We assess your field operations to design intelligent scheduling, mobile workforce solutions, and predictive maintenance strategies that optimize technician utilization and improve first-time fix rates.',
    capabilities: ['Intelligent work order scheduling', 'Mobile technician enablement', 'Asset and inventory management', 'Predictive maintenance'],
    caseStudy: {
      industry: 'Utilities Company',
      challenge: 'Inefficient scheduling causing technician downtime. First-time fix rate below 60%. Customer appointment windows too wide.',
      solution: 'Deployed Field Service with AI-powered scheduling, mobile app for technicians, and parts inventory management. Optimized routes and skills-based assignment.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '30% increase in first-time fix rates'
    }, {
      icon: 'Gauge',
      label: 'Optimized technician utilization'
    }, {
      icon: 'DollarSign',
      label: 'Reduced operational costs'
    }, {
      icon: 'Clock',
      label: 'Tighter appointment windows'
    }]
  },
  CPQ: {
    title: 'CPQ (Configure, Price, Quote)',
    description: 'Our discovery process maps your product catalog and pricing complexity to design CPQ solutions that guide sales reps through configuration, apply accurate pricing rules, and generate quotes in minutes instead of days.',
    capabilities: ['Product configuration rules', 'Dynamic pricing and discounting', 'Guided selling workflows', 'Approval process automation'],
    caseStudy: {
      industry: 'Manufacturing Equipment Supplier',
      challenge: 'Complex product configurations causing quote errors. Sales reps spending hours building quotes. Approval bottlenecks delaying deals.',
      solution: 'Built CPQ with guided configuration, automated pricing rules, and streamlined approvals. Integrated with ERP for real-time inventory and cost data.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '50% faster quote generation'
    }, {
      icon: 'CheckCircle',
      label: 'Reduced pricing errors'
    }, {
      icon: 'DollarSign',
      label: 'Improved deal profitability'
    }, {
      icon: 'Workflow',
      label: 'Automated approval workflows'
    }]
  },
  MuleSoft: {
    title: 'MuleSoft Integration',
    description: 'We map your technology landscape and design API-led integration architecture that connects Salesforce with your entire ecosystem. Our approach creates reusable integration assets that accelerate future connectivity.',
    capabilities: ['API design and development', 'System integration architecture', 'Real-time data synchronization', 'Integration governance'],
    caseStudy: {
      industry: 'Financial Services Institution',
      challenge: 'Salesforce disconnected from core banking systems. Manual data entry causing errors. No real-time visibility into customer accounts.',
      solution: 'Built MuleSoft integration layer connecting Salesforce to 8 backend systems. Created reusable APIs for account data, transactions, and customer profiles.'
    },
    outcomes: [{
      icon: 'Layers',
      label: 'Seamless system connectivity'
    }, {
      icon: 'Zap',
      label: 'Real-time data availability'
    }, {
      icon: 'Target',
      label: 'Reduced integration complexity'
    }, {
      icon: 'CheckCircle',
      label: 'Reusable API assets'
    }]
  },
  Tableau: {
    title: 'Tableau Analytics',
    description: 'Through analytics workshops, we identify your key business questions and design Tableau dashboards that transform Salesforce data into actionable insights. Our visualizations empower data-driven decision-making across your organization.',
    capabilities: ['Interactive dashboard design', 'Advanced analytics and forecasting', 'Self-service analytics enablement', 'Embedded analytics integration'],
    caseStudy: {
      industry: 'Healthcare Network',
      challenge: 'Executives relying on static reports delivered weekly. No visibility into real-time performance. Data analysts spending days building custom reports.',
      solution: 'Built executive dashboards with real-time Salesforce data, predictive analytics for patient volume, and self-service reporting for department heads.'
    },
    outcomes: [{
      icon: 'Brain',
      label: 'Data-driven decision making'
    }, {
      icon: 'Zap',
      label: 'Real-time business insights'
    }, {
      icon: 'Users',
      label: 'Democratized analytics access'
    }, {
      icon: 'LineChart',
      label: 'Predictive forecasting'
    }]
  },
  'Einstein AI': {
    title: 'Einstein AI',
    description: 'We run AI readiness workshops to identify high-value use cases for predictive and generative AI within your Salesforce processes. Our implementations embed intelligence directly into workflows for automated decision-making.',
    capabilities: ['Predictive lead and opportunity scoring', 'AI-powered recommendations', 'Natural language processing', 'Custom AI model development'],
    caseStudy: {
      industry: 'B2B Technology Company',
      challenge: 'Sales team wasting time on low-quality leads. No visibility into which opportunities would close. Manual prioritization causing missed revenue.',
      solution: 'Implemented Einstein Lead Scoring and Opportunity Insights. AI now predicts close probability and recommends next best actions for each deal.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Intelligent automation at scale'
    }, {
      icon: 'Brain',
      label: 'Predictive insights for better decisions'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced user productivity'
    }, {
      icon: 'Target',
      label: 'AI-driven prioritization'
    }]
  },
  Identity: {
    title: 'Identity & Access Management',
    description: 'Our security workshops assess your current access controls and design enterprise-grade identity solutions that balance security with user experience. We implement SSO, MFA, and role-based access that scales with your organization.',
    capabilities: ['Single sign-on (SSO) implementation', 'Multi-factor authentication', 'Role-based access control', 'Identity governance'],
    caseStudy: {
      industry: 'Professional Services Firm',
      challenge: 'Multiple login credentials causing password fatigue. No MFA creating security risks. Manual user provisioning taking days.',
      solution: 'Implemented SSO with existing identity provider, enforced MFA for all users, and automated user provisioning based on role changes.'
    },
    outcomes: [{
      icon: 'Shield',
      label: 'Enhanced security posture'
    }, {
      icon: 'Users',
      label: 'Streamlined user access'
    }, {
      icon: 'Lock',
      label: 'Compliance with security standards'
    }, {
      icon: 'Workflow',
      label: 'Automated provisioning'
    }]
  },
  'Financial Services Cloud': {
    title: 'Financial Services Cloud',
    description: 'We partner with wealth management, banking, and insurance firms to design industry-specific solutions that deliver holistic client views, ensure regulatory compliance, and enhance advisor productivity.',
    capabilities: ['Client relationship management', 'Financial account aggregation', 'Compliance and regulatory reporting', 'Advisor productivity tools'],
    caseStudy: {
      industry: 'Wealth Management Firm',
      challenge: 'Advisors juggling multiple systems to view client portfolios. No unified household view. Compliance reporting manual and error-prone.',
      solution: 'Deployed Financial Services Cloud with household relationship mapping, integrated account data from custodians, and automated compliance workflows.'
    },
    outcomes: [{
      icon: 'Users',
      label: 'Holistic client view'
    }, {
      icon: 'FileCheck',
      label: 'Regulatory compliance'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced advisor efficiency'
    }, {
      icon: 'Shield',
      label: 'Automated compliance reporting'
    }]
  },
  'Health Cloud': {
    title: 'Health Cloud',
    description: 'Through healthcare-focused workshops, we design patient-centric solutions that improve care coordination, enhance provider collaboration, and create seamless patient experiences across the care continuum.',
    capabilities: ['Patient 360 view', 'Care plan management', 'Provider collaboration', 'Health data integration'],
    caseStudy: {
      industry: 'Multi-Specialty Clinic Network',
      challenge: 'Patient information siloed across specialties. Care coordination happening via phone and fax. No visibility into patient care journey.',
      solution: 'Implemented Health Cloud with unified patient records, care plan templates, and provider collaboration tools. Integrated with EHR for real-time clinical data.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Improved patient outcomes'
    }, {
      icon: 'Users',
      label: 'Coordinated care delivery'
    }, {
      icon: 'CheckCircle',
      label: 'Enhanced patient engagement'
    }, {
      icon: 'Activity',
      label: 'Real-time care visibility'
    }]
  },
  'Automotive Cloud': {
    title: 'Automotive Cloud',
    description: 'We work with automotive retailers and manufacturers to design connected customer experiences that span vehicle sales, service, and ownership. Our solutions optimize inventory, streamline service operations, and personalize the customer journey.',
    capabilities: ['Vehicle inventory management', 'Service appointment scheduling', 'Customer journey orchestration', 'Dealer performance analytics'],
    caseStudy: {
      industry: 'Auto Dealership Group',
      challenge: 'Disconnected sales and service experiences. Manual service scheduling causing conflicts. No visibility into customer vehicle ownership history.',
      solution: 'Deployed Automotive Cloud connecting sales, service, and parts. Automated service scheduling with technician availability. Created unified customer vehicle profiles.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Enhanced customer experience'
    }, {
      icon: 'BarChart3',
      label: 'Optimized inventory turnover'
    }, {
      icon: 'DollarSign',
      label: 'Increased service revenue'
    }, {
      icon: 'Clock',
      label: 'Streamlined scheduling'
    }]
  },
  'Omni Studio': {
    title: 'Omni Studio',
    description: 'We design industry-specific digital experiences using OmniStudio low-code tools, creating reusable components and guided workflows that accelerate development while maintaining consistency across your applications.',
    capabilities: ['FlexCard and OmniScript development', 'Industry data model configuration', 'Integration procedures', 'Guided user experiences'],
    caseStudy: {
      industry: 'Insurance Provider',
      challenge: 'Custom development taking months for each new product launch. Inconsistent user experiences across lines of business.',
      solution: 'Built reusable OmniStudio components for policy applications, claims processing, and customer service. Reduced development time by 60%.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Rapid application development'
    }, {
      icon: 'CheckCircle',
      label: 'Consistent user experiences'
    }, {
      icon: 'Target',
      label: 'Industry-specific solutions'
    }, {
      icon: 'Layers',
      label: 'Reusable component library'
    }]
  },
  Slack: {
    title: 'Slack Integration',
    description: 'Our workshops identify collaboration bottlenecks and design Slack integrations that bring Salesforce data and workflows into your team conversations, enabling real-time collaboration and faster decision-making.',
    capabilities: ['Salesforce-Slack integration', 'Workflow automation in Slack', 'Real-time notifications', 'Collaborative selling'],
    caseStudy: {
      industry: 'SaaS Company',
      challenge: 'Sales teams switching between Slack and Salesforce constantly. Deal updates happening in silos. No visibility into customer conversations.',
      solution: 'Integrated Salesforce with Slack for deal rooms, automated opportunity updates in channels, and enabled Salesforce actions directly from Slack.'
    },
    outcomes: [{
      icon: 'Users',
      label: 'Faster team collaboration'
    }, {
      icon: 'Zap',
      label: 'Real-time business updates'
    }, {
      icon: 'TrendingUp',
      label: 'Improved productivity'
    }, {
      icon: 'Activity',
      label: 'Centralized deal visibility'
    }]
  },
  APIs: {
    title: 'API Development',
    description: 'We design and build custom APIs that extend Salesforce capabilities and enable seamless integrations with your technology ecosystem. Our approach creates secure, scalable, well-documented APIs that support your business needs.',
    capabilities: ['REST and SOAP API development', 'Custom API endpoints', 'API security and governance', 'Integration architecture'],
    caseStudy: {
      industry: 'E-commerce Platform',
      challenge: 'Third-party systems unable to access Salesforce data. Manual data exports creating delays. No standardized integration approach.',
      solution: 'Built custom REST APIs for order management, customer data, and inventory. Implemented OAuth security and rate limiting. Created developer documentation.'
    },
    outcomes: [{
      icon: 'Layers',
      label: 'Seamless system integration'
    }, {
      icon: 'Zap',
      label: 'Extended platform capabilities'
    }, {
      icon: 'Shield',
      label: 'Secure data exchange'
    }, {
      icon: 'FileCheck',
      label: 'Well-documented APIs'
    }]
  },
  Heroku: {
    title: 'Heroku Development',
    description: 'Through technical workshops, we identify use cases where custom applications on Heroku can extend your Salesforce ecosystem. We build scalable, cloud-native applications that integrate seamlessly with your Salesforce data.',
    capabilities: ['Custom application development', 'Heroku Connect integration', 'Scalable architecture design', 'DevOps and deployment'],
    caseStudy: {
      industry: 'Media Company',
      challenge: 'Customer-facing portal requiring real-time content delivery and complex business logic beyond Salesforce capabilities.',
      solution: 'Built custom Node.js application on Heroku with Heroku Connect for bi-directional Salesforce sync. Scaled to handle 100K+ daily users.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Extended platform capabilities'
    }, {
      icon: 'Gauge',
      label: 'Scalable custom applications'
    }, {
      icon: 'Layers',
      label: 'Seamless Salesforce integration'
    }, {
      icon: 'Activity',
      label: 'High-performance delivery'
    }]
  },
  'Custom Apps': {
    title: 'Custom Application Development',
    description: 'We collaborate with your teams to design and build bespoke Salesforce applications tailored to your unique business requirements. Using Lightning Web Components and Apex, we create solutions that feel native to Salesforce.',
    capabilities: ['Lightning Web Components', 'Apex development', 'Custom UI/UX design', 'Application lifecycle management'],
    caseStudy: {
      industry: 'Logistics Company',
      challenge: 'Complex route optimization and load planning requirements not met by standard Salesforce functionality.',
      solution: 'Built custom application with LWC interface for route planning, Apex algorithms for optimization, and real-time GPS tracking integration.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Tailored business solutions'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced user productivity'
    }, {
      icon: 'Sparkles',
      label: 'Competitive differentiation'
    }, {
      icon: 'CheckCircle',
      label: 'Native Salesforce experience'
    }]
  },
  Orchestration: {
    title: 'Process Orchestration',
    description: 'We map complex multi-step processes that span teams and systems, then design orchestration workflows that coordinate work, handle exceptions, and provide visibility into process performance.',
    capabilities: ['Multi-step process design', 'Cross-functional workflow coordination', 'Exception handling', 'Process monitoring and optimization'],
    caseStudy: {
      industry: 'Financial Services Firm',
      challenge: 'Loan approval process involving 5 departments, 12 steps, and multiple systems. Average cycle time of 14 days with frequent errors.',
      solution: 'Designed orchestration workflow coordinating credit checks, underwriting, compliance review, and approval. Automated handoffs and exception routing.'
    },
    outcomes: [{
      icon: 'Workflow',
      label: 'Streamlined operations'
    }, {
      icon: 'Clock',
      label: 'Reduced process cycle times'
    }, {
      icon: 'Users',
      label: 'Improved collaboration'
    }, {
      icon: 'Activity',
      label: 'Real-time process visibility'
    }]
  },
  'Sales Execution': {
    title: 'Sales Execution',
    description: 'We analyze your sales motions and design Sales Execution solutions that automate cadences, track engagement, and provide the insights your team needs to prioritize high-value activities and accelerate pipeline.',
    capabilities: ['Sales cadence automation', 'Engagement tracking', 'Pipeline management', 'Sales analytics'],
    caseStudy: {
      industry: 'B2B Software Company',
      challenge: 'SDRs manually tracking outreach. No visibility into engagement effectiveness. Inconsistent follow-up causing lost opportunities.',
      solution: 'Implemented automated sales cadences with multi-channel touchpoints, engagement scoring, and intelligent task prioritization based on buyer signals.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Increased sales velocity'
    }, {
      icon: 'BarChart3',
      label: 'Better pipeline visibility'
    }, {
      icon: 'Brain',
      label: 'Data-driven sales strategies'
    }, {
      icon: 'Target',
      label: 'Improved conversion rates'
    }]
  },
  Messaging: {
    title: 'Messaging',
    description: 'Our workshops identify opportunities to engage customers through their preferred messaging channels. We design conversational workflows for SMS, WhatsApp, and messaging platforms that feel personal and drive action.',
    capabilities: ['Multi-channel messaging', 'Conversational workflows', 'Message template management', 'Messaging analytics'],
    caseStudy: {
      industry: 'Healthcare Provider',
      challenge: 'Appointment reminders via phone calls causing staff burden. High no-show rates. Patients wanting text-based communication.',
      solution: 'Deployed SMS and WhatsApp messaging for appointment reminders, confirmations, and follow-ups. Built conversational flows for rescheduling.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Enhanced customer engagement'
    }, {
      icon: 'Zap',
      label: 'Real-time communication'
    }, {
      icon: 'CheckCircle',
      label: 'Improved response rates'
    }, {
      icon: 'Clock',
      label: 'Reduced no-show rates'
    }]
  },
  'Live Chat': {
    title: 'Live Chat',
    description: 'We design intelligent live chat solutions that provide instant customer support while optimizing agent efficiency. Our implementations include chatbot integration, smart routing, and seamless escalation to human agents.',
    capabilities: ['Chat widget customization', 'Chat routing and assignment', 'Chatbot integration', 'Chat analytics'],
    caseStudy: {
      industry: 'E-commerce Retailer',
      challenge: 'Customers abandoning carts due to unanswered questions. Support team overwhelmed during peak hours. No 24/7 coverage.',
      solution: 'Implemented live chat with AI chatbot for common questions, intelligent routing to product specialists, and proactive chat triggers based on behavior.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Instant customer support'
    }, {
      icon: 'Clock',
      label: 'Reduced wait times'
    }, {
      icon: 'TrendingUp',
      label: 'Increased customer satisfaction'
    }, {
      icon: 'DollarSign',
      label: 'Reduced cart abandonment'
    }]
  },
  'Marketing Automation': {
    title: 'Marketing Automation',
    description: 'Through marketing operations workshops, we design automation workflows that nurture leads, trigger personalized campaigns based on behavior, and drive conversions at scale while maintaining the human touch.',
    capabilities: ['Lead nurturing campaigns', 'Email automation', 'Behavioral triggers', 'Campaign performance tracking'],
    caseStudy: {
      industry: 'B2B Services Company',
      challenge: 'Leads going cold after initial contact. Manual follow-up inconsistent. No visibility into which campaigns drove conversions.',
      solution: 'Built automated nurture campaigns triggered by lead behavior, implemented lead scoring, and created attribution reporting for campaign ROI.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Increased lead conversion'
    }, {
      icon: 'Target',
      label: 'Personalized engagement'
    }, {
      icon: 'Gauge',
      label: 'Marketing efficiency'
    }, {
      icon: 'BarChart3',
      label: 'Clear ROI attribution'
    }]
  },
  'Operational AI': {
    title: 'Operational AI',
    description: 'We identify operational processes where AI can automate decisions, detect anomalies, and optimize outcomes. Our implementations embed intelligence into daily workflows for proactive issue resolution and continuous improvement.',
    capabilities: ['Process automation with AI', 'Intelligent recommendations', 'Anomaly detection', 'Predictive analytics'],
    caseStudy: {
      industry: 'Manufacturing Company',
      challenge: 'Quality issues discovered too late in production. Manual inspection processes missing defects. Reactive problem-solving.',
      solution: 'Deployed AI models to predict quality issues based on production parameters, detect anomalies in real-time, and recommend corrective actions.'
    },
    outcomes: [{
      icon: 'Brain',
      label: 'Automated decision-making'
    }, {
      icon: 'Gauge',
      label: 'Operational efficiency'
    }, {
      icon: 'Shield',
      label: 'Proactive issue resolution'
    }, {
      icon: 'TrendingUp',
      label: 'Continuous optimization'
    }]
  },
  'Model Builder': {
    title: 'Model Builder',
    description: 'Our data science workshops identify opportunities for custom AI models tailored to your specific business context. We build, train, and deploy models that deliver predictions and insights unique to your operations.',
    capabilities: ['Custom prediction models', 'Model training and optimization', 'Model deployment and monitoring', 'AI governance'],
    caseStudy: {
      industry: 'Telecommunications Provider',
      challenge: 'Generic churn models not capturing nuances of their customer base. Unable to predict which customers would leave and why.',
      solution: 'Built custom churn prediction model using their specific customer data, behavior patterns, and service usage. Achieved 85% prediction accuracy.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Tailored AI solutions'
    }, {
      icon: 'TrendingUp',
      label: 'Improved prediction accuracy'
    }, {
      icon: 'Brain',
      label: 'Business-specific intelligence'
    }, {
      icon: 'Shield',
      label: 'Governed AI deployment'
    }]
  }
};
const architectureLayers = [{
  name: 'Foundation',
  services: ['Salesforce Platform', 'Data Cloud', 'Identity'],
  color: 'rgba(79, 12, 154, 0.14)',
  borderColor: 'rgba(79, 12, 154, 0.45)',
  glowColor: '#4f0c9a'
}, {
  name: 'Core Layer',
  services: ['Sales Cloud', 'Service Cloud', 'Marketing Cloud'],
  color: 'rgba(79, 12, 154, 0.12)',
  borderColor: 'rgba(79, 12, 154, 0.4)',
  glowColor: '#4f0c9a'
}, {
  name: 'Platform Layer',
  services: ['Experience Cloud', 'Revenue Cloud', 'Field Service', 'CPQ', 'Financial Services Cloud', 'Health Cloud', 'Automotive Cloud', 'Omni Studio', 'Slack'],
  color: 'rgba(79, 12, 154, 0.1)',
  borderColor: 'rgba(79, 12, 154, 0.35)',
  glowColor: '#4f0c9a'
}, {
  name: 'Integration Layer',
  services: ['MuleSoft', 'APIs', 'Heroku', 'Custom Apps'],
  color: 'rgba(79, 12, 154, 0.08)',
  borderColor: 'rgba(79, 12, 154, 0.3)',
  glowColor: '#4f0c9a'
}, {
  name: 'Automation Layer',
  services: ['Agentforce', 'Flow', 'Omni-Channel', 'Orchestration', 'Sales Execution', 'Messaging', 'Live Chat', 'Marketing Automation', 'Operational AI'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.3)',
  glowColor: '#15c8ff'
}, {
  name: 'Intelligence Layer',
  services: ['Tableau', 'Einstein AI', 'Model Builder'],
  color: 'rgba(250, 255, 49, 0.08)',
  borderColor: 'rgba(250, 255, 49, 0.3)',
  glowColor: '#faff31'
}];
export const About: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedGlowColor, setSelectedGlowColor] = useState<string>('#4f0c9a');
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const titleY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  // Sunrise glow continues into this section
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.3, 0.6], [0.3, 0.2, 0]);
  const handleServiceClick = (service: string, glowColor: string) => {
    setSelectedService(service);
    setSelectedGlowColor(glowColor);
  };
  const closeModal = () => {
    setSelectedService(null);
  };
  return <section id="about" ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden" data-id="element-16">
      {/* Floating Energy Orbs */}
      <FloatingOrbs count={12} data-id="element-17" />

      {/* Continuing sunrise glow from Hero */}
      <motion.div className="absolute top-0 left-0 right-0 h-[40vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(250, 255, 49, 0.12) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-18" />

      {/* Bottom glow to connect sections */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.18) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-19" />

      {/* Ambient yellow glow throughout */}
      <div className="absolute inset-0 pointer-events-none opacity-30" data-id="element-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-electric-yellow/10 rounded-full blur-3xl" data-id="element-21" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-electric-yellow/8 rounded-full blur-3xl" data-id="element-22" />
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-23">
        {/* Centered Title & Subheading */}
        <motion.div className="text-center mb-16" style={{
        y: titleY,
        opacity: titleOpacity
      }} data-id="element-24">
          <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-4 text-white" initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} data-id="element-25">
            Built across the entire{' '}
            <span className="text-electric-yellow" data-id="element-26">Salesforce stack</span>
          </motion.h2>

          <motion.p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-27">
            From core clouds to integrations, analytics, and AI — we design and
            evolve the complete system.
          </motion.p>
        </motion.div>

        {/* 2-Column Layout: Stats (40%) + Pyramid (60%) on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 max-w-7xl mx-auto relative" data-id="element-28">
          {/* Left Column - Statistics Tiles (40% = 2 cols) */}
          <motion.div className="grid grid-cols-2 gap-4 lg:grid-cols-1 lg:space-y-0 lg:gap-4 lg:col-span-2" initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-29">
            {/* Stat 1 - Projects */}
            <motion.div className="relative backdrop-blur-sm rounded-lg border p-5 lg:mt-0" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} data-id="element-30">
              <div className="flex items-baseline gap-2 mb-2" data-id="element-31">
                <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-32">
                  250+
                </span>
                <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31',
                boxShadow: '0 0 8px #faff31'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-33" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed" data-id="element-34">
                Successfully delivered projects across our team
              </p>
            </motion.div>

            {/* Stat 2 - Experience */}
            <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-35">
              <div className="flex items-baseline gap-2 mb-2" data-id="element-36">
                <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-37">
                  30+
                </span>
                <span className="text-sm text-white/60" data-id="element-38">years</span>
                <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#15c8ff',
                boxShadow: '0 0 8px #15c8ff'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5
              }} data-id="element-39" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed" data-id="element-40">
                Collective Salesforce ecosystem experience
              </p>
            </motion.div>

            {/* Stat 3 - Full-Stack Delivery */}
            <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-41">
              <div className="flex items-baseline gap-2 mb-2" data-id="element-42">
                <span className="text-2xl font-heading font-bold text-electric-yellow leading-tight" data-id="element-43">
                  Full-Stack Salesforce Delivery
                </span>
                <motion.div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                backgroundColor: '#4f0c9a',
                boxShadow: '0 0 8px #4f0c9a'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1
              }} data-id="element-44" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed" data-id="element-45">
                From platform architecture to AI integration, we deliver
                complete Salesforce solutions
              </p>
            </motion.div>

            {/* Stat 4 - APAC */}
            <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} viewport={{
            once: true
          }} data-id="element-46">
              <div className="flex items-baseline gap-2 mb-2" data-id="element-47">
                <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-48">
                  APAC
                </span>
                <span className="text-sm text-white/60" data-id="element-49">focused</span>
                <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31',
                boxShadow: '0 0 8px #faff31'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.5
              }} data-id="element-50" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed" data-id="element-51">
                Supporting organisations across Singapore and Australia
              </p>
            </motion.div>
          </motion.div>

          {/* Right Column - Pyramid with Mascot Above (60% = 3 cols) */}
          <motion.div className="relative lg:col-span-3" initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-52">
            {/* Mascot Centered Above Pyramid - Desktop Only */}
            <motion.div className="hidden lg:flex flex-col items-center mb-6" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-53">
              {/* Mascot Image */}
              <motion.img src="https://cdn.magicpatterns.com/uploads/o9mAFQYrCvgHdm4iMPAe9j/ChatGPT_Image_Feb_13,_2026,_07_35_13_PM.png" alt="Deploid Mascot" className="w-16 h-16 md:w-20 md:h-20 object-contain" style={{
              filter: 'drop-shadow(0 0 15px rgba(250, 255, 49, 0.5))'
            }} initial={{
              rotate: -5
            }} animate={{
              rotate: [5, -5, 5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-54" />

              {/* Speech Bubble Below Mascot */}
              <motion.div className="relative mt-3" initial={{
              scale: 0.8,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} viewport={{
              once: true
            }} data-id="element-55">
                <div className="relative px-4 py-2 rounded-lg backdrop-blur-sm border-2" style={{
                backgroundColor: 'rgba(250, 255, 49, 0.15)',
                borderColor: '#faff31',
                boxShadow: '0 0 20px rgba(250, 255, 49, 0.3)'
              }} data-id="element-56">
                  <p className="text-sm font-medium whitespace-nowrap" style={{
                  color: '#faff31'
                }} data-id="element-57">
                    Select a Cloud to Learn More
                  </p>
                  {/* Speech bubble tail pointing down */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{
                  backgroundColor: 'rgba(250, 255, 49, 0.15)',
                  borderRight: '2px solid #faff31',
                  borderBottom: '2px solid #faff31'
                }} data-id="element-58" />
                </div>
              </motion.div>

              {/* Sparkles Around Mascot */}
              <motion.div className="absolute inset-0 pointer-events-none" animate={{
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-59">
                <div className="absolute top-0 -left-4 w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31'
              }} data-id="element-60" />
                <div className="absolute top-2 right-0 w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#15c8ff'
              }} data-id="element-61" />
                <div className="absolute bottom-8 -left-2 w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31'
              }} data-id="element-62" />
              </motion.div>
            </motion.div>

            {/* Desktop: Horizontal Layer Bands */}
            <div className="hidden lg:flex flex-col gap-2" data-id="element-63">
              {architectureLayers.slice().reverse().map((layer, index) => {
              // Scale duration with chip count so all rows drift at the same px/s
              const basePxPerSecond = 30 / 3; // 10s per service-set
              const driftDuration = layer.services.length * basePxPerSecond;
              // 4x repetition ensures sparse layers (3 chips) always fill the container
              const quadServices = [...layer.services, ...layer.services, ...layer.services, ...layer.services];
              return <motion.div key={layer.name} className="relative w-full rounded-lg border backdrop-blur-sm overflow-hidden flex items-center" style={{
                backgroundColor: layer.color,
                borderColor: layer.borderColor,
                borderWidth: '2px',
                boxShadow: `0 0 15px ${layer.glowColor}30, inset 0 0 15px ${layer.glowColor}08`,
                minHeight: '72px'
              }} initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.08
              }} viewport={{
                once: true
              }} data-id="element-64">
                      <div className="absolute top-0 left-0 right-0 h-[1.5px] pointer-events-none" style={{
                  background: `linear-gradient(to right, ${layer.glowColor}80, ${layer.glowColor}30, transparent)`
                }} data-id="element-65" />
                      <div className="absolute bottom-0 left-0 right-0 h-[1.5px] pointer-events-none" style={{
                  background: `linear-gradient(to right, transparent, ${layer.glowColor}30, ${layer.glowColor}80)`
                }} data-id="element-66" />
                      <div className="flex-shrink-0 flex items-center gap-2 px-4 z-10" style={{
                  width: '140px'
                }} data-id="element-67">
                        <motion.div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                    backgroundColor: layer.glowColor,
                    boxShadow: `0 0 8px ${layer.glowColor}`
                  }} animate={{
                    scale: [1, 1.4, 1],
                    opacity: [0.7, 1, 0.7]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3
                  }} data-id="element-68" />
                        <span className="text-[11px] font-heading font-semibold text-white leading-tight" data-id="element-69">
                          {layer.name}
                        </span>
                      </div>
                      <div className="flex-shrink-0 w-[1px] self-stretch my-3" style={{
                  backgroundColor: `${layer.glowColor}30`
                }} data-id="element-70" />
                      <div className="flex-1 overflow-hidden relative" style={{
                  height: '72px'
                }} data-id="element-71">
                        <div className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none" style={{
                    background: `linear-gradient(to right, rgba(28,20,61,0.85), transparent)`
                  }} data-id="element-72" />
                        <div className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none" style={{
                    background: `linear-gradient(to left, rgba(28,20,61,0.85), transparent)`
                  }} data-id="element-73" />
                        {/* 4x repetition: animate -50% = moves 2 sets, always 2 sets filling the view */}
                        <div className="chip-drift absolute flex items-center gap-2 px-4 h-full" style={{
                    animationDuration: `${driftDuration}s`,
                    width: 'max-content'
                  }} data-id="element-74">
                          {quadServices.map((service, serviceIndex) => <button key={serviceIndex} onClick={() => handleServiceClick(service, layer.glowColor)} className="flex-shrink-0 rounded border backdrop-blur-sm cursor-pointer transition-all hover:scale-105" style={{
                      backgroundColor: 'rgba(28, 20, 61, 0.55)',
                      borderColor: layer.borderColor.replace('0.45', '0.25').replace('0.4', '0.25').replace('0.35', '0.25').replace('0.3', '0.25'),
                      padding: '0.3rem 0.65rem',
                      boxShadow: `0 0 6px ${layer.glowColor}20`
                    }} onMouseEnter={e => {
                      const row = e.currentTarget.closest('.chip-drift') as HTMLElement | null;
                      if (row) row.style.animationPlayState = 'paused';
                    }} onMouseLeave={e => {
                      const row = e.currentTarget.closest('.chip-drift') as HTMLElement | null;
                      if (row) row.style.animationPlayState = 'running';
                    }} data-id="element-75">
                              <span className="text-white/90 font-medium whitespace-nowrap leading-tight" style={{
                        fontSize: '10px'
                      }} data-id="element-76">
                                {service}
                              </span>
                            </button>)}
                        </div>
                      </div>
                    </motion.div>;
            })}
            </div>

            {/* Mobile: Mascot Above + Flat Stacked Bars */}
            <div className="lg:hidden" data-id="element-77">
              {/* Mobile Mascot (duplicate for mobile) */}
              <motion.div className="flex flex-col items-center mb-6" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} viewport={{
              once: true
            }} data-id="element-78">
                <motion.img src="https://cdn.magicpatterns.com/uploads/o9mAFQYrCvgHdm4iMPAe9j/ChatGPT_Image_Feb_13,_2026,_07_35_13_PM.png" alt="Deploid Mascot" className="w-16 h-16 object-contain" style={{
                filter: 'drop-shadow(0 0 15px rgba(250, 255, 49, 0.5))'
              }} animate={{
                rotate: [5, -5, 5]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-79" />
                <motion.div className="relative mt-3" initial={{
                scale: 0.8,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.5,
                delay: 0.3
              }} viewport={{
                once: true
              }} data-id="element-80">
                  <div className="relative px-4 py-2 rounded-lg backdrop-blur-sm border-2" style={{
                  backgroundColor: 'rgba(250, 255, 49, 0.15)',
                  borderColor: '#faff31',
                  boxShadow: '0 0 20px rgba(250, 255, 49, 0.3)'
                }} data-id="element-81">
                    <p className="text-sm font-medium text-center" style={{
                    color: '#faff31'
                  }} data-id="element-82">
                      Select a Cloud to Learn More
                    </p>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{
                    backgroundColor: 'rgba(250, 255, 49, 0.15)',
                    borderRight: '2px solid #faff31',
                    borderBottom: '2px solid #faff31'
                  }} data-id="element-83" />
                  </div>
                </motion.div>
              </motion.div>

              {/* Flat Stacked Bars */}
              <div className="space-y-3" data-id="element-84">
                {architectureLayers.slice().reverse().map((layer, index) => <motion.div key={index} className="relative backdrop-blur-sm rounded-lg border p-4 cursor-pointer" style={{
                backgroundColor: layer.color,
                borderColor: layer.borderColor,
                boxShadow: `0 0 15px ${layer.glowColor}40, inset 0 0 15px ${layer.glowColor}10`
              }} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} whileTap={{
                scale: 0.98
              }} data-id="element-85">
                      <div className="flex items-center justify-between mb-2" data-id="element-86">
                        <div className="flex items-center gap-2" data-id="element-87">
                          <motion.div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: layer.glowColor,
                      boxShadow: `0 0 8px ${layer.glowColor}`
                    }} animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-88" />
                          <h3 className="text-sm font-heading font-semibold text-white" data-id="element-89">
                            {layer.name}
                          </h3>
                        </div>
                        <span className="text-xs text-white/60" data-id="element-90">
                          {layer.services.length} services
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-id="element-91">
                        {layer.services.map((service, serviceIndex) => <button key={serviceIndex} onClick={e => {
                    e.stopPropagation();
                    handleServiceClick(service, layer.glowColor);
                  }} className="px-2 py-1 rounded border backdrop-blur-sm text-xs text-white/90 font-medium" style={{
                    backgroundColor: 'rgba(28, 20, 61, 0.5)',
                    borderColor: layer.borderColor.replace('0.3', '0.2').replace('0.45', '0.2').replace('0.4', '0.2').replace('0.35', '0.2')
                  }} data-id="element-92">
                            {service}
                          </button>)}
                      </div>
                    </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom note */}
        <motion.div className="mt-12 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} viewport={{
        once: true
      }} data-id="element-93">
          <p className="text-white/60 text-sm" data-id="element-94">
            End-to-end Salesforce architecture, from foundation to intelligence
          </p>
        </motion.div>
      </div>

      {/* Expertise Modal */}
      <AnimatePresence data-id="element-95">
        {selectedService && serviceExpertise[selectedService] && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={closeModal} data-id="element-96" />

            {/* Modal */}
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-97">
              <motion.div className="relative w-full max-w-3xl backdrop-blur-xl rounded-2xl border-2 p-6 md:p-8 pointer-events-auto max-h-[85vh] overflow-y-auto" style={{
            backgroundColor: 'rgba(28, 20, 61, 0.95)',
            borderColor: selectedGlowColor,
            boxShadow: `0 0 40px ${selectedGlowColor}60, inset 0 0 40px ${selectedGlowColor}10`
          }} initial={{
            scale: 0.9,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} data-id="element-98">
                {/* Close button */}
                <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full border transition-all hover:scale-110" style={{
              borderColor: selectedGlowColor,
              backgroundColor: 'rgba(28, 20, 61, 0.8)'
            }} data-id="element-99">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{
                color: selectedGlowColor
              }} data-id="element-100">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-101" />
                  </svg>
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6" data-id="element-102">
                  <motion.div className="w-3 h-3 rounded-full" style={{
                backgroundColor: selectedGlowColor,
                boxShadow: `0 0 15px ${selectedGlowColor}`
              }} animate={{
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-103" />
                  <h2 className="text-3xl font-heading font-bold" style={{
                color: selectedGlowColor
              }} data-id="element-104">
                    {serviceExpertise[selectedService].title}
                  </h2>
                </div>

                {/* Workshop Intro */}
                <div className="mb-8 pl-4 border-l-2" style={{
              borderColor: selectedGlowColor
            }} data-id="element-105">
                  <p className="text-white/90 text-lg leading-relaxed" data-id="element-106">
                    {serviceExpertise[selectedService].description}
                  </p>
                </div>

                {/* Capabilities Grid */}
                <div className="mb-8" data-id="element-107">
                  <h3 className="text-white font-heading font-semibold text-xl mb-4" data-id="element-108">
                    Our Capabilities
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-109">
                    {serviceExpertise[selectedService].capabilities.map((capability, index) => <motion.div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.05
                }} data-id="element-110">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{
                    backgroundColor: selectedGlowColor,
                    boxShadow: `0 0 8px ${selectedGlowColor}`
                  }} data-id="element-111" />
                          <span className="text-white/80 text-sm leading-relaxed" data-id="element-112">
                            {capability}
                          </span>
                        </motion.div>)}
                  </div>
                </div>

                {/* Outcomes */}
                <div data-id="element-113">
                  <h3 className="text-white font-heading font-semibold text-xl mb-4" data-id="element-114">
                    Outcomes
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-115">
                    {serviceExpertise[selectedService].outcomes.map((outcome, index) => {
                  const IconComponent = iconMap[outcome.icon] || CheckCircle;
                  return <motion.div key={index} className="flex items-center gap-3 p-3.5 rounded-lg border backdrop-blur-sm" style={{
                    backgroundColor: `${selectedGlowColor}10`,
                    borderColor: `${selectedGlowColor}30`
                  }} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.3 + index * 0.05
                  }} data-id="element-116">
                            <IconComponent className="w-5 h-5 flex-shrink-0" style={{
                      color: selectedGlowColor
                    }} data-id="element-117" />
                            <span className="text-white/90 text-sm font-medium leading-tight" data-id="element-118">
                              {outcome.label}
                            </span>
                          </motion.div>;
                })}
                  </div>
                </div>

                {/* Holographic edge effect */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              boxShadow: `inset 0 0 60px ${selectedGlowColor}20`
            }} data-id="element-119" />
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
};